import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import releaseNoteApi from '../../services/releaseNoteApi';
import { PageHeader, TWInput, TWButton, EmptyState } from '../../components/ui';
import { countLabel, feedQuery, hasMore, FEED_PATH } from './release-note-model';
import { markReleaseNotesSeen } from './release-notes-seen';
import ReleaseNoteMeta from './components/ReleaseNoteMeta';
import TagChips from './components/TagChips';
import ReleaseNotesSkeleton from './components/ReleaseNotesSkeleton';
import './release-notes.css';

const SHELL_CLASS =
  'release-notes-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';
const SEARCH_DEBOUNCE_MS = 400;
const LOAD_ERROR = "Couldn't load updates";

// One feed row (spec §4.3): meta column left, cover + summary + link right.
const Entry = ({ note }) => {
  const to = `${FEED_PATH}/${note.slug}`;
  return (
    <article className="grid gap-x-12 gap-y-6 py-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:py-10">
      <ReleaseNoteMeta note={note} titleAs="h2" titleTo={to} />
      <div className="flex min-w-0 flex-col gap-4">
        {note.coverimageurl ? (
          // Decorative duplicate of the title link: hidden from assistive tech.
          <Link
            to={to}
            aria-hidden="true"
            tabIndex={-1}
            className="block overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900"
          >
            <img
              src={note.coverimageurl}
              alt=""
              loading="lazy"
              className="block aspect-video w-full object-cover"
            />
          </Link>
        ) : null}
        {note.summary ? (
          <p className="max-w-[62ch] text-base leading-relaxed text-slate-700 dark:text-slate-300">
            {note.summary}
          </p>
        ) : null}
        <Link
          to={to}
          className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
        >
          Read the note
          <span className="sr-only">: {note.title}</span>
          <ChevronRightIcon className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </article>
  );
};

const ReleaseNotes = () => {
  const [params, setParams] = useSearchParams();
  const tag = params.get('tag') || '';
  const query = params.get('q') || '';

  const [search, setSearch] = useState(query);
  const [tags, setTags] = useState([]);
  const [items, setItems] = useState([]);
  const [paged, setPaged] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);
  // The debounce timer fires up to 400 ms after the render that scheduled it;
  // setSearchParams applies its updater to that render's params, so a tag
  // clicked meanwhile would be erased. Always call the latest one.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  // Typed search → URL (?q=) after a pause; the URL is the single source the
  // fetch effect reads, so a reload or a shared link keeps the filter.
  useEffect(() => {
    if (search === query) return undefined;
    const t = setTimeout(() => {
      setParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (search) next.set('q', search);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // URL → box: back/forward or a link to the bare feed changes ?q= without a
  // keystroke. After a typed search the debounce has already made them equal.
  useEffect(() => {
    setSearch(query);
  }, [query]);

  useEffect(() => {
    releaseNoteApi
      .tags()
      .then((res) => setTags((res.data?.data || []).map((t) => t.name)))
      .catch(() => setTags([]));
  }, []);

  const fetchPage = useCallback(
    async (pageNumber, { append } = { append: false }) => {
      const id = ++requestId.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await releaseNoteApi.list(feedQuery({ pageNumber, tag, search: query }));
        if (id !== requestId.current) return;
        const data = res.data?.data || [];
        setPaged(res.data || null);
        // A note published between the two requests shifts page 2 by one, so
        // its first entry can repeat page 1's last: keep each note once.
        setItems((prev) => (append ? [...prev, ...data.filter((n) => !prev.some((p) => p.id === n.id))] : data));
        setFailed(false);
        // Only an unfiltered first page proves the newest note was on screen.
        if (!append && !tag && !query && data[0]?.publishedat) markReleaseNotesSeen(data[0].publishedat);
      } catch {
        if (id !== requestId.current) return;
        message.error(LOAD_ERROR);
        if (!append) {
          setItems([]);
          setPaged(null);
          setFailed(true);
        }
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tag, query],
  );

  // Any filter change starts over at page 1.
  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const setTag = (next) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set('tag', next);
        else p.delete('tag');
        return p;
      },
      { replace: true },
    );

  const clearFilters = () => {
    setSearch('');
    setParams({}, { replace: true });
  };

  const filtered = Boolean(tag || query);
  const more = hasMore(paged);

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto flex max-w-5xl flex-col gap-7">
        <PageHeader
          center
          title={
            <span className="rn-display block text-3xl font-semibold leading-[1.1] tracking-tight sm:text-[2.5rem]">
              What's new
            </span>
          }
          description="Product updates for your restaurant, newest first. Every note here is already live in your dashboard, kiosk and ordering site."
        />

        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          {tags.length > 0 ? <TagChips tags={tags} active={tag} onChange={setTag} /> : null}
          <div className="flex w-full items-center gap-3 sm:ml-auto sm:w-auto">
            <div className="w-full sm:w-60">
              <TWInput
                size="sm"
                leftIcon={MagnifyingGlassIcon}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search updates"
                aria-label="Search updates"
              />
            </div>
            {!loading && paged ? (
              <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                {countLabel(paged.totalRecords)}
              </span>
            ) : null}
          </div>
        </div>

        {loading ? (
          <ReleaseNotesSkeleton rows={3} />
        ) : failed ? (
          <EmptyState
            variant="dashed"
            title="Couldn't load updates"
            description="Check your connection and try again."
            action={<TWButton variant="primary" label="Retry" onClick={() => fetchPage(1)} />}
          />
        ) : items.length === 0 ? (
          <EmptyState
            variant="dashed"
            icon={SparklesIcon}
            title={filtered ? 'No updates match' : 'Nothing new yet'}
            description={
              filtered ? 'Try another tag or search.' : 'Product updates will appear here as they ship.'
            }
            action={filtered ? <TWButton variant="link" label="Clear filters" onClick={clearFilters} /> : null}
          />
        ) : (
          <>
            <section className="reveal divide-y divide-slate-200 dark:divide-slate-700">
              {items.map((note) => (
                <Entry key={note.id} note={note} />
              ))}
            </section>
            {more ? (
              <div className="flex justify-center border-t border-slate-200 pt-7 dark:border-slate-700">
                <TWButton
                  size="lg"
                  label="Load more"
                  loading={loadingMore}
                  onClick={() => fetchPage(Number(paged.pageNumber) + 1, { append: true })}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ReleaseNotes;
